import React, {
  useCallback,
  useEffect,
  useMemo,
  useState,
} from "react";
import { useNavigate } from "react-router-dom";
import { Box, Chip, Typography } from "@mui/material";
import { GraphQLClient } from "graphql-request";
import SecurityOutlinedIcon from "@mui/icons-material/SecurityOutlined";
import BadgeOutlinedIcon from "@mui/icons-material/BadgeOutlined";

import Header from "../../components/Header";
import DeleteUserModal from "../../components/DeleteUserModal";
import TableActions from "../../components/TableActions";
import ActionButton from "../../components/ActionButton";
import ThemedDataGrid from "../../components/ThemedDataGrid";
import { useTenant } from "../../context/TenantContext";
import { useAuth } from "../../auth/AuthContext";
import { useSnackbar } from "../../components/snackbarProvider.jsx";

import { graphqlEndpoint } from "../../config";
import { UserRole } from "../../constants/userRoles.ts";
import { READ_TENANT_USERS_BY_TENANT } from "../../graph/tenantUsers/queries";
import { ARCHIVE_TENANT_USER } from "../../graph/tenantUsers/mutations";

const client = new GraphQLClient(graphqlEndpoint);

const allowedTenantRoles = [
  UserRole.TENANT_ADMIN,
  UserRole.TENANT_STAFF,
];

const ManageUsers = () => {
  const navigate = useNavigate();
  const { tenantId, tenantName } = useTenant();
  const { user } = useAuth();
  const { setSnackbar } = useSnackbar();

  const [users, setUsers] = useState([]);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);

  const isTenantAdmin = user?.role === UserRole.TENANT_ADMIN;
  const effectiveTenantId = tenantId;
  const effectiveTenantName = tenantName;

  const fetchUsers = useCallback(async () => {
    if (!effectiveTenantId) {
      setUsers([]);
      return;
    }

    try {
      const data = await client.request(
        READ_TENANT_USERS_BY_TENANT,
        {
          tenantId: effectiveTenantId,
        }
      );

      const formattedUsers = (
        data.readTenantUsersByTenant || []
      ).map((tenantUser) => {
        const globalUser = tenantUser.user;

        const fallbackName = `${
          globalUser?.firstName || ""
        } ${globalUser?.lastName || ""}`.trim();

        return {
          // The DataGrid row ID and archive ID must be the TenantUser ID.
          id: tenantUser.id,

          // The edit screen still requires the global User ID.
          userId: tenantUser.userId || globalUser?.id,

          tenantId: tenantUser.tenantId,
          accessRole: tenantUser.accessRole,
          tenantUserArchivedAt: tenantUser.archivedAt,

          firstName: globalUser?.firstName || "",
          lastName: globalUser?.lastName || "",
          email: globalUser?.email || "",
          hourlyRate: globalUser?.hourlyRate,
          verifiedEmail: Boolean(globalUser?.verifiedEmail),
          role: globalUser?.role,
          tenantID: globalUser?.tenantID,
          contactID: globalUser?.contactID || "",
          notes: globalUser?.notes,
          invitedBy: globalUser?.invitedBy,
          createdAt: globalUser?.createdAt,
          updatedAt: globalUser?.updatedAt,
          archivedAt: globalUser?.archivedAt,

          displayName: tenantUser.displayName,
          name:
            tenantUser.displayName ||
            fallbackName ||
            globalUser?.email ||
            tenantUser.userId,
        };
      });

      setUsers(formattedUsers);
    } catch (err) {
      console.error("Failed to fetch tenant users:", err);
      setUsers([]);

      setSnackbar({
        open: true,
        severity: "error",
        message: "Failed to load users",
      });
    }
  }, [effectiveTenantId, setSnackbar]);

  useEffect(() => {
    fetchUsers();
  }, [fetchUsers]);

  const handleOpenDeleteModal = (selectedRow) => {
    setSelectedUser(selectedRow);
    setDeleteModalOpen(true);
  };

  const handleCloseDeleteModal = () => {
    setDeleteModalOpen(false);
    setSelectedUser(null);
  };

  const handleConfirmDelete = async () => {
    if (!selectedUser?.id) {
      return;
    }

    try {
      await client.request(ARCHIVE_TENANT_USER, {
        id: selectedUser.id,
      });

      await fetchUsers();
      handleCloseDeleteModal();

      setSnackbar({
        open: true,
        severity: "success",
        message: "User removed from tenant successfully",
      });
    } catch (err) {
      console.error("Failed to archive tenant user:", err);

      setSnackbar({
        open: true,
        severity: "error",
        message: "Failed to remove user from tenant",
      });
    }
  };

  const filteredUsers = useMemo(
    () =>
      users.filter(
        (tenantUser) =>
          !tenantUser.tenantUserArchivedAt &&
          allowedTenantRoles.includes(tenantUser.role)
      ),
    [users]
  );

  const formatRoleLabel = (role) => {
    switch (role) {
      case UserRole.TENANT_ADMIN:
        return "Tenant Admin";

      case UserRole.TENANT_STAFF:
        return "Tenant Staff";

      default:
        return role || "Unknown";
    }
  };

  const renderRoleChip = (role) => {
    const isAdmin = role === UserRole.TENANT_ADMIN;

    return (
      <Chip
        icon={
          isAdmin ? (
            <SecurityOutlinedIcon />
          ) : (
            <BadgeOutlinedIcon />
          )
        }
        label={formatRoleLabel(role)}
        size="small"
        color={isAdmin ? "secondary" : "default"}
        variant={isAdmin ? "filled" : "outlined"}
      />
    );
  };

  const columns = [
    {
      field: "name",
      headerName: "Name",
      flex: 1.2,
      cellClassName: "name-column--cell",
    },
    {
      field: "email",
      headerName: "Email",
      flex: 1.4,
    },
    {
      field: "contactID",
      headerName: "Contact ID",
      flex: 1,
    },
    {
      field: "verifiedEmail",
      headerName: "Verified",
      flex: 0.8,
      valueGetter: (value) => (value ? "Yes" : "No"),
    },
    {
      field: "role",
      headerName: "Role",
      flex: 1,
      renderCell: ({ row }) => renderRoleChip(row.role),
    },
    {
      field: "actions",
      headerName: "Actions",
      flex: 1,
      sortable: false,
      filterable: false,
      renderCell: ({ row }) => (
        <TableActions
          onEdit={() => {
            if (!row.userId) {
              setSnackbar({
                open: true,
                severity: "error",
                message: "Unable to identify the user to edit",
              });
              return;
            }

            navigate(`/users/edit/${row.userId}`, {
              state: {
                redirectPath: "/manageUsers",
                tenantUserId: row.id,
              },
            });
          }}
          onDelete={() => handleOpenDeleteModal(row)}
          hideCreate
        />
      ),
    },
  ];

  return (
    <Box>
      <Box
        display="flex"
        justifyContent="space-between"
        alignItems="center"
      >
        <Header
          title="USERS"
          subtitle={
            effectiveTenantName
              ? `Manage users for ${effectiveTenantName}`
              : "Manage tenant users"
          }
        />

        <ActionButton
          tone="gold"
          onClick={() =>
            navigate("/createUser", {
              state: {
                redirectPath: "/manageUsers",
                fixedRole: isTenantAdmin
                  ? UserRole.TENANT_STAFF
                  : null,
                fixedTenantId: isTenantAdmin
                  ? effectiveTenantId
                  : null,
              },
            })
          }
          disabled={!effectiveTenantId}
        >
          Create User
        </ActionButton>
      </Box>

      {!effectiveTenantId ? (
        <Box mt={4}>
          <Typography variant="h6">
            Select a tenant in the top bar to view users.
          </Typography>
        </Box>
      ) : (
        <ThemedDataGrid
          rows={filteredUsers}
          columns={columns}
        />
      )}

      <DeleteUserModal
        open={deleteModalOpen}
        onClose={handleCloseDeleteModal}
        onConfirm={handleConfirmDelete}
        user={selectedUser}
      />
    </Box>
  );
};

export default ManageUsers;